import React from 'react';
import PropTypes from 'prop-types';
import styled from 'libs/styled';
import getQueryParams from '@fogia/core-web/utils/getQueryParams';
import Link from '@fogia/core-web/components/Link';
import PaginationButton from 'components/Pagination/PaginationButton';
import TextStyles from 'components/Text';
import { useLocation } from 'react-router-dom';

const Wrapper = styled('div')``;

const NavRow = styled('div')`
    display: flex;
    align-items: center;
    gap: 20px;
    height: 30px;
    overflow: hidden;
`;

const NumberButtonsWrapper = styled('div')`
    display: flex;
    align-items: center;
    height: 100%;
    white-space: nowrap;
`;

const PageLink = styled(Link)`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    height: 30px;
    padding: 10px;
    white-space: nowrap;
    color: ${(props) => (props.active ? 'var(--ui-secondary-aa)' : 'var(--ui-primary)')};
`;

const DotElement = styled('div')`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 10px;
    color: var(--ui-primary);
`;

const Pagination = ({ page, pageCount, handleClick }) => {
    const location = useLocation();

    const buttons = [];

    // Responsive typography keys
    const numberTypography = ['Primary/Mobile/13_100_510_-1', null, null, null, null, 'Primary/15_120_510_-1'];

    let min;
    let max;
    if (pageCount <= 4) {
        min = 0;
        max = pageCount;
    } else {
        min = page >= 3 ? page - 1 : 0;
        max = pageCount > min + 4 ? min + 4 : pageCount;
        if (page >= pageCount - 2) {
            min = pageCount - 4;
            max = pageCount;
        }
    }

    const getQueryUrl = (pagination) => {
        pagination += 1;
        const query = getQueryParams(location);

        if (pagination === 1) {
            delete query.page;
        } else {
            query.page = pagination;
        }
        return (
            location.pathname +
            (Object.entries(query).length
                ? `?${Object.entries(query)
                      .map((q) => q.join('='))
                      .join('&')}`
                : '')
        );
    };

    if (page >= 3 && pageCount > 4) {
        buttons.push(
            <PageLink
                data-is-first
                key="paginationStart"
                to={getQueryUrl(0)}
                onClick={handleClick && handleClick.bind(this, 0)}
            >
                <TextStyles typography={numberTypography}>1</TextStyles>
            </PageLink>,
        );

        if (pageCount !== 5) {
            buttons.push(
                <DotElement key="empty1">
                    <TextStyles typography={numberTypography}>…</TextStyles>
                </DotElement>,
            );
        }
    }

    for (let index = min; index < max; index++) {
        buttons.push(
            <PageLink
                key={index}
                to={getQueryUrl(index)}
                active={page === index}
                onClick={page === index ? null : handleClick && handleClick.bind(this, index)}
            >
                <TextStyles typography={numberTypography}>{index + 1}</TextStyles>
            </PageLink>,
        );
    }
    if (pageCount > max) {
        if (pageCount !== 5) {
            buttons.push(
                <DotElement key="empty2">
                    <TextStyles typography={numberTypography}>…</TextStyles>
                </DotElement>,
            );
        }

        buttons.push(
            <PageLink
                data-is-last
                key="pageCount"
                to={getQueryUrl(pageCount - 1)}
                onClick={handleClick && handleClick.bind(this, pageCount - 1)}
            >
                <TextStyles typography={numberTypography}>{pageCount}</TextStyles>
            </PageLink>,
        );
    }

    return (
        <Wrapper
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="30px"
            p={['70px 0', null, null, null, null, '100px 0']}
        >
            <NavRow>
                <PaginationButton
                    active={page > 0}
                    to={page > 0 ? getQueryUrl(page - 1) : getQueryUrl(page)}
                    direction="left"
                    onClick={handleClick && handleClick.bind(this, page - 1)}
                />
                <NumberButtonsWrapper gap={['3px', null, null, null, null, '5px']}>{buttons}</NumberButtonsWrapper>
                <PaginationButton
                    active={page + 1 < pageCount}
                    to={page + 1 < pageCount ? getQueryUrl(page + 1) : getQueryUrl(page)}
                    direction="right"
                    onClick={handleClick && handleClick.bind(this, page + 1)}
                />
            </NavRow>
        </Wrapper>
    );
};

Pagination.propTypes = {
    handleClick: PropTypes.func,
    page: PropTypes.number.isRequired,
    pageCount: PropTypes.number.isRequired,
};

Pagination.defaultProps = {
    handleClick: () => null,
};

export default Pagination;
